import { create } from 'zustand';
import { arrayMove } from '@dnd-kit/sortable';
import { servicesContainer } from '@/core/di/services-container';
import type { OperatorApp } from '@/features/operator-selection/types/operator-types';
import type {
  DraftStep,
  KioskFlow,
  KioskFlowOperatorContext,
  KioskFlowValidationError,
  KioskFlowVersion,
  KioskImageOption,
  KioskLayoutOption,
} from '../types/kiosk-flow-types';
import { KioskFlowConflictError, KioskFlowValidationFailure, PublishError } from '../types/kiosk-flow-errors';
import type { PublishStage } from '../types/kiosk-flow-errors';
import { createEmptyDraftStep, isDraftDirty, toDraftSteps } from '../utils/flow-steps';

interface KioskFlowState {
  selectedOperator: OperatorApp | null;
  // Each operator has at most one flow; null means it hasn't created one yet
  flow: KioskFlow | null;
  isLoadingFlow: boolean;
  isCreatingFlow: boolean;
  isRenamingFlow: boolean;
  isDeletingFlow: boolean;
  flowError: string | null;
  createFlowError: string | null;
  renameFlowError: string | null;
  deleteFlowError: string | null;
  // Editable copy of flow.screenFlow — saved with saveFlow
  draftSteps: DraftStep[];
  isSavingFlow: boolean;
  saveFlowError: string | null;
  // Operator's XML layouts from Asset Manager
  layouts: KioskLayoutOption[];
  isLoadingLayouts: boolean;
  layoutsError: string | null;
  // Operator-level images from Asset Manager (title + file name) — the banner options
  images: KioskImageOption[];
  isLoadingImages: boolean;
  imagesError: string | null;
  // Editable promo banner file name ('' = none) — saved with saveFlow
  draftBanner: string;
  // Publish = prepare → push to the kiosks → record the version
  isPublishing: boolean;
  publishError: string | null;
  // Which step failed — a push failure may still have reached the kiosks
  publishFailedStage: PublishStage | null;
  // Per-step reasons the backoffice refused to publish the saved draft
  publishValidationErrors: KioskFlowValidationError[];
  // Set when the push reached the kiosks but recording the version failed — retryRecord sends it again
  unrecordedJson: string | null;
  // The record failed because the draft changed since prepare — retrying can't work, publish again
  recordConflict: boolean;
  isRecording: boolean;
}

export type PreviewPublishResult =
  | { json: string }
  | { error: string; validationErrors: KioskFlowValidationError[] };

interface KioskFlowActions {
  selectOperator: (operator: OperatorApp | null) => void;
  loadFlow: () => Promise<void>;
  createFlow: (name: string) => Promise<boolean>;
  renameFlow: (name: string) => Promise<boolean>;
  deleteFlow: () => Promise<boolean>;
  addStep: () => void;
  updateStep: (id: string, changes: Partial<Omit<DraftStep, 'id'>>) => void;
  removeStep: (id: string) => void;
  moveStep: (activeId: string, overId: string) => void;
  saveFlow: () => Promise<boolean>;
  loadLayouts: () => Promise<void>;
  loadImages: () => Promise<void>;
  setDraftBanner: (fileName: string) => void;
  publishFlow: () => Promise<KioskFlowVersion | null>;
  retryRecord: () => Promise<KioskFlowVersion | null>;
  dismissUnrecorded: () => void;
  previewPublish: () => Promise<PreviewPublishResult>;
  clearCreateFlowError: () => void;
  clearRenameFlowError: () => void;
  clearDeleteFlowError: () => void;
  reset: () => void;
}

export type KioskFlowStore = KioskFlowState & KioskFlowActions;

const initialState: KioskFlowState = {
  selectedOperator: null,
  flow: null,
  isLoadingFlow: false,
  isCreatingFlow: false,
  isRenamingFlow: false,
  isDeletingFlow: false,
  flowError: null,
  createFlowError: null,
  renameFlowError: null,
  deleteFlowError: null,
  draftSteps: [],
  isSavingFlow: false,
  saveFlowError: null,
  layouts: [],
  isLoadingLayouts: false,
  layoutsError: null,
  images: [],
  isLoadingImages: false,
  imagesError: null,
  draftBanner: '',
  isPublishing: false,
  publishError: null,
  publishFailedStage: null,
  publishValidationErrors: [],
  unrecordedJson: null,
  recordConflict: false,
  isRecording: false,
};

const clearedPublishState = {
  isPublishing: false,
  publishError: null,
  publishFailedStage: null,
  publishValidationErrors: [],
  unrecordedJson: null,
  recordConflict: false,
  isRecording: false,
} satisfies Partial<KioskFlowState>;

// The kiosks run a flow whose version isn't recorded yet and Retry record can still fix it.
// Switching operators would drop the JSON, so the switch is blocked until it's recorded or dismissed.
export function hasPendingRecord(state: Pick<KioskFlowState, 'unrecordedJson' | 'recordConflict'>): boolean {
  return !!state.unrecordedJson && !state.recordConflict;
}

export const PENDING_RECORD_REASON = 'Retry recording the last publish or dismiss it first';

// The steps or the banner differ from the saved draft
export function isFlowDirty(state: Pick<KioskFlowState, 'flow' | 'draftSteps' | 'draftBanner'>): boolean {
  if (!state.flow) return false;
  return (
    isDraftDirty(state.draftSteps, state.flow.screenFlow) ||
    state.draftBanner.trim() !== (state.flow.promoBannerFileName ?? '').trim()
  );
}

// Incremented on every flow request and operator change — a response whose
// generation is no longer current is discarded, so switching operators quickly
// can't let an older operator's flow overwrite the newer one.
let flowRequestGen = 0;
// Same idea for the layouts and images lists
let layoutsRequestGen = 0;
let imagesRequestGen = 0;

// Rows whose timeout target pointed at a step that's no longer in the flow get it cleared
function clearMissingTargets(steps: DraftStep[]): DraftStep[] {
  const inFlow = new Set(steps.map((s) => s.step).filter(Boolean));
  return steps.map((s) => (s.timeoutTarget && !inFlow.has(s.timeoutTarget) ? { ...s, timeoutTarget: '' } : s));
}

// Responses without screenFlow (rename, publish, record) keep the saved steps and banner
function keepDraft(next: KioskFlow, prev: KioskFlow | null): KioskFlow {
  if (next.screenFlow) return next;
  return {
    ...next,
    screenFlow: prev?.screenFlow,
    promoBannerFileName: next.promoBannerFileName ?? prev?.promoBannerFileName,
  };
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function toContext(operator: OperatorApp): KioskFlowOperatorContext {
  return { operatorId: operator.operatorId, appId: operator.appId };
}

export const useKioskFlowStore = create<KioskFlowStore>((set, get) => ({
  ...initialState,

  selectOperator: (operator) => {
    if (hasPendingRecord(get())) return;
    ++flowRequestGen;
    ++layoutsRequestGen;
    ++imagesRequestGen;
    set({
      selectedOperator: operator,
      flow: null,
      isLoadingFlow: false,
      flowError: null,
      createFlowError: null,
      renameFlowError: null,
      deleteFlowError: null,
      draftSteps: [],
      saveFlowError: null,
      layouts: [],
      isLoadingLayouts: false,
      layoutsError: null,
      images: [],
      isLoadingImages: false,
      imagesError: null,
      draftBanner: '',
      ...clearedPublishState,
    });
    if (operator) {
      void get().loadFlow();
      void get().loadLayouts();
      void get().loadImages();
    }
  },

  loadFlow: async () => {
    const operator = get().selectedOperator;
    if (!operator) return;

    const gen = ++flowRequestGen;
    set({ isLoadingFlow: true, flowError: null });
    try {
      const services = servicesContainer.getServices();
      const flow = await services.getKioskFlowUseCase.execute(toContext(operator));
      if (gen !== flowRequestGen) return; // discard stale response
      set({
        flow,
        draftSteps: toDraftSteps(flow?.screenFlow),
        draftBanner: flow?.promoBannerFileName ?? '',
        isLoadingFlow: false,
      });
    } catch (error) {
      if (gen !== flowRequestGen) return;
      set({
        flowError: error instanceof Error ? error.message : 'Failed to load kiosk flow',
        isLoadingFlow: false,
      });
    }
  },

  createFlow: async (name) => {
    const operator = get().selectedOperator;
    if (!operator) return false;

    set({ isCreatingFlow: true, createFlowError: null });
    try {
      const services = servicesContainer.getServices();
      const flow = await services.createKioskFlowUseCase.execute(toContext(operator), name);
      // Operator changed while the request was in flight — don't show the flow under the wrong operator
      if (get().selectedOperator?.operatorId !== operator.operatorId) {
        set({ isCreatingFlow: false });
        return false;
      }
      set({
        flow,
        draftSteps: toDraftSteps(flow.screenFlow),
        draftBanner: flow.promoBannerFileName ?? '',
        isCreatingFlow: false,
      });
      return true;
    } catch (error) {
      set({
        createFlowError: error instanceof Error ? error.message : 'Failed to create kiosk flow',
        isCreatingFlow: false,
      });
      return false;
    }
  },

  renameFlow: async (name) => {
    const operator = get().selectedOperator;
    if (!operator) return false;

    set({ isRenamingFlow: true, renameFlowError: null });
    try {
      const services = servicesContainer.getServices();
      const saved = await services.renameKioskFlowUseCase.execute(toContext(operator), name);
      if (get().selectedOperator?.operatorId !== operator.operatorId) {
        set({ isRenamingFlow: false });
        return false;
      }
      // A rename response without screenFlow must not wipe the steps or the banner
      set({ flow: keepDraft(saved, get().flow), isRenamingFlow: false });
      return true;
    } catch (error) {
      set({
        renameFlowError: error instanceof Error ? error.message : 'Failed to rename kiosk flow',
        isRenamingFlow: false,
      });
      return false;
    }
  },

  deleteFlow: async () => {
    const operator = get().selectedOperator;
    if (!operator) return false;
    // Deleting clears the publish state, which would drop the JSON Retry record needs
    if (hasPendingRecord(get())) {
      set({ deleteFlowError: PENDING_RECORD_REASON });
      return false;
    }

    set({ isDeletingFlow: true, deleteFlowError: null });
    try {
      const services = servicesContainer.getServices();
      await services.deleteKioskFlowUseCase.execute(toContext(operator));
      if (get().selectedOperator?.operatorId !== operator.operatorId) {
        set({ isDeletingFlow: false });
        return false;
      }
      // Back to the "Create New Flow" state
      set({
        flow: null,
        draftSteps: [],
        draftBanner: '',
        saveFlowError: null,
        isDeletingFlow: false,
        ...clearedPublishState,
      });
      return true;
    } catch (error) {
      set({
        deleteFlowError: error instanceof Error ? error.message : 'Failed to delete kiosk flow',
        isDeletingFlow: false,
      });
      return false;
    }
  },

  addStep: () => set((state) => ({ draftSteps: [...state.draftSteps, createEmptyDraftStep()] })),

  updateStep: (id, changes) =>
    set((state) => ({
      draftSteps: clearMissingTargets(state.draftSteps.map((s) => (s.id === id ? { ...s, ...changes } : s))),
    })),

  removeStep: (id) =>
    set((state) => ({ draftSteps: clearMissingTargets(state.draftSteps.filter((s) => s.id !== id)) })),

  // Order only — timeout targets reference steps by name, so they stay valid
  moveStep: (activeId, overId) =>
    set((state) => {
      const from = state.draftSteps.findIndex((s) => s.id === activeId);
      const to = state.draftSteps.findIndex((s) => s.id === overId);
      if (from === -1 || to === -1 || from === to) return state;
      return { draftSteps: arrayMove(state.draftSteps, from, to) };
    }),

  saveFlow: async () => {
    const { selectedOperator: operator, flow, draftSteps, draftBanner } = get();
    if (!operator || !flow) return false;

    set({ isSavingFlow: true, saveFlowError: null });
    try {
      const services = servicesContainer.getServices();
      const saved = await services.saveKioskFlowUseCase.execute(toContext(operator), flow, draftSteps, draftBanner);
      if (get().selectedOperator?.operatorId !== operator.operatorId) {
        set({ isSavingFlow: false });
        return false;
      }
      // Rows or banner edited while the save was in flight stay as they are
      const unchanged = get().draftSteps === draftSteps && get().draftBanner === draftBanner;
      set({
        flow: saved,
        ...(unchanged
          ? { draftSteps: toDraftSteps(saved.screenFlow), draftBanner: saved.promoBannerFileName ?? '' }
          : {}),
        // Errors from the last publish attempt were about the previous draft
        publishValidationErrors: [],
        isSavingFlow: false,
      });
      return true;
    } catch (error) {
      set({
        saveFlowError: error instanceof Error ? error.message : 'Failed to save kiosk flow',
        isSavingFlow: false,
      });
      return false;
    }
  },

  loadLayouts: async () => {
    const operator = get().selectedOperator;
    if (!operator) return;

    const gen = ++layoutsRequestGen;
    set({ isLoadingLayouts: true, layoutsError: null });
    try {
      const services = servicesContainer.getServices();
      const layouts = await services.getKioskLayoutsUseCase.execute(toContext(operator));
      if (gen !== layoutsRequestGen) return;
      set({ layouts, isLoadingLayouts: false });
    } catch (error) {
      if (gen !== layoutsRequestGen) return;
      set({
        layoutsError: error instanceof Error ? error.message : 'Failed to load layouts',
        isLoadingLayouts: false,
      });
    }
  },

  loadImages: async () => {
    const operator = get().selectedOperator;
    if (!operator) return;

    const gen = ++imagesRequestGen;
    set({ isLoadingImages: true, imagesError: null });
    try {
      const services = servicesContainer.getServices();
      const images = await services.getKioskImagesUseCase.execute(toContext(operator));
      if (gen !== imagesRequestGen) return;
      set({ images, isLoadingImages: false });
    } catch (error) {
      if (gen !== imagesRequestGen) return;
      set({ imagesError: errorMessage(error, 'Failed to load images'), isLoadingImages: false });
    }
  },

  setDraftBanner: (fileName) => set({ draftBanner: fileName }),

  publishFlow: async () => {
    const operator = get().selectedOperator;
    if (!operator || !get().flow) return null;

    set({ ...clearedPublishState, isPublishing: true });
    try {
      const services = servicesContainer.getServices();
      // Runs to the end even if the operator changes — the kiosks are being updated either way
      const { version, flow } = await services.publishKioskFlowUseCase.execute(toContext(operator));
      if (get().selectedOperator?.operatorId !== operator.operatorId) return null;
      // Only the publish fields change — the draft rows are what was just published
      set({ flow: keepDraft(flow, get().flow), isPublishing: false });
      return version;
    } catch (error) {
      if (get().selectedOperator?.operatorId !== operator.operatorId) return null;
      const message = errorMessage(error, 'Failed to publish the flow');
      if (error instanceof PublishError && error.stage === 'record') {
        set({
          isPublishing: false,
          publishError: message,
          publishFailedStage: 'record',
          unrecordedJson: error.pushedJson,
          recordConflict: error.reason instanceof KioskFlowConflictError,
        });
      } else {
        const reason = error instanceof PublishError ? error.reason : error;
        set({
          isPublishing: false,
          publishError: message,
          publishFailedStage: error instanceof PublishError ? error.stage : null,
          publishValidationErrors: reason instanceof KioskFlowValidationFailure ? reason.validationErrors : [],
        });
      }
      return null;
    }
  },

  retryRecord: async () => {
    const { selectedOperator: operator, unrecordedJson } = get();
    if (!operator || !unrecordedJson) return null;

    set({ isRecording: true });
    try {
      const services = servicesContainer.getServices();
      const { version, flow } = await services.recordKioskFlowVersionUseCase.execute(
        toContext(operator),
        unrecordedJson
      );
      if (get().selectedOperator?.operatorId !== operator.operatorId) return null;
      set({ ...clearedPublishState, flow: keepDraft(flow, get().flow) });
      return version;
    } catch (error) {
      if (get().selectedOperator?.operatorId !== operator.operatorId) return null;
      set({
        isRecording: false,
        publishError: errorMessage(error, 'Failed to record the published version'),
        recordConflict: error instanceof KioskFlowConflictError,
      });
      return null;
    }
  },

  // Gives up on recording the pushed flow — the kiosks keep running it, but it gets no version.
  // Also clears the conflict banner, where retrying can't help.
  dismissUnrecorded: () => {
    if (get().isRecording) return;
    set({ ...clearedPublishState });
  },

  // Data lookup for the Preview JSON dialog — returns directly, page state is untouched
  previewPublish: async () => {
    const operator = get().selectedOperator;
    if (!operator) return { error: 'An operator must be selected', validationErrors: [] };
    try {
      const services = servicesContainer.getServices();
      return { json: await services.preparePublishKioskFlowUseCase.execute(toContext(operator)) };
    } catch (error) {
      return {
        error: errorMessage(error, 'Failed to prepare the flow for publishing'),
        validationErrors: error instanceof KioskFlowValidationFailure ? error.validationErrors : [],
      };
    }
  },

  clearCreateFlowError: () => set({ createFlowError: null }),
  clearRenameFlowError: () => set({ renameFlowError: null }),
  clearDeleteFlowError: () => set({ deleteFlowError: null }),

  reset: () => {
    ++flowRequestGen;
    ++layoutsRequestGen;
    ++imagesRequestGen;
    set(initialState);
  },
}));
